import { DOCUMENT, Injectable, inject } from '@angular/core';
import { OAuthService, type OAuthStorage } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SsoAuthService {
  private oauthService = inject(OAuthService);

  private consumedState: string | null = null;

  initialize(): Promise<void> {
    this.configureOauth();
    return this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => undefined)
      .catch((error: unknown) => {
        console.error(error);
      });
  }

  tryLogin(): Promise<boolean> {
    return this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(() => this.oauthService.hasValidAccessToken())
      .catch((error: unknown) => {
        console.error(error);
        return false;
      });
  }

  startLogin(state: string): void {
    this.oauthService.initCodeFlow(state);
  }

  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  getPostLoginRedirectPath(): string | null {
    const state = this.oauthService.state;

    if (!state || state === this.consumedState) {
      return null;
    }

    try {
      return decodeURIComponent(state);
    } catch {
      return state;
    }
  }

  consumeRedirectPath(): void {
    this.consumedState = this.oauthService.state ?? null;
  }

  logOut(): void {
    this.oauthService.logOut();
  }

  private configureOauth(): void {
    this.oauthService.configure({
      issuer: environment.sso.issuer,
      clientId: environment.sso.clientId,
      redirectUri: this.getRedirectUrl(),
      responseType: 'code',
      scope: 'openid profile email offline_access',
      showDebugInformation: !environment.production,
      strictDiscoveryDocumentValidation: false,
    });

    this.oauthService.setupAutomaticSilentRefresh();
  }

  private getRedirectUrl(): string {
    const browserWindow = this.getBrowserWindow();
    if (!browserWindow) {
      return '/';
    }

    return browserWindow.location.origin;
  }

  private getBrowserWindow(): Window | null {
    return typeof window !== 'undefined' ? window : null;
  }
}

export const createInMemoryStorage = (): OAuthStorage => {
  const storage = new Map<string, string>();
  return {
    getItem: (key: string) => storage.get(key) ?? null,
    removeItem: (key: string) => {
      storage.delete(key);
    },
    setItem: (key: string, value: string) => {
      storage.set(key, value);
    },
  };
};

export const oAuthStorageFactory = (): OAuthStorage => {
  const document = inject(DOCUMENT, { optional: true });
  return document?.defaultView?.sessionStorage ?? createInMemoryStorage();
};
